import { useState } from "react";
import { Dialog } from "@/components/ui/design-system";
import { Button } from "@/components/ui/button";
import { useDanas } from "@/lib/dan";
import { porukaDohvata } from "@/lib/greske";
import { errorMessage } from "@/lib/api";
import { sickLeave, type SickLeave } from "@/lib/bolovanja";

const control = "block min-h-11 w-full rounded-lg border p-2";

export function SickLeaveCreateDialog({ workerId, workerName, siteId, onClose, onChanged }: {
  workerId: string; workerName: string; siteId: string; onClose: () => void; onChanged: () => Promise<void>;
}) {
  const today = useDanas();
  const [start, setStart] = useState(today), [end, setEnd] = useState(today);
  const [mode, setMode] = useState("ongoing");
  const [file, setFile] = useState<File | null>(null), [created, setCreated] = useState<SickLeave | null>(null);
  const [error, setError] = useState(""), [busy, setBusy] = useState(false), [uploaded, setUploaded] = useState(false);
  async function submit() {
    if (busy) return;
    setBusy(true); setError("");
    try {
      const row = created ?? await sickLeave.create({ radnik_id: workerId, gradiliste_id: siteId, datum_od: start, datum_do: mode === "ongoing" ? null : mode === "today" ? start : end });
      setCreated(row);
      await onChanged();
      if (file && !uploaded) {
        await sickLeave.upload(row.id, file);
        setUploaded(true);
        await onChanged();
      }
    } catch (cause) { setError(errorMessage(cause, porukaDohvata(cause))); }
    finally { setBusy(false); }
  }
  async function undo() {
    if (!created || busy) return;
    setBusy(true); setError("");
    try { await sickLeave.cancel(created.id); await onChanged(); onClose(); }
    catch (cause) { setError(errorMessage(cause, porukaDohvata(cause))); }
    finally { setBusy(false); }
  }
  return <Dialog title={`Bolovanje — ${workerName}`} onClose={() => { if (!busy) onClose(); }}>
    <div className="space-y-4">
      {created ? <p role="status" className="rounded-lg border border-amber-300 bg-amber-50 p-3">Bolovanje je prijavljeno. {uploaded ? "Doznaka je dodana." : "Doznaka još nije dodana."}</p> : <>
        <label className="block">Od<input className={control} type="date" value={start} max={today} onChange={e => { if (e.target.value) setStart(e.target.value); }} /></label>
        <fieldset><legend className="font-medium">Trajanje</legend><div className="flex flex-wrap gap-2">{[["ongoing", "Još traje"], ["today", "Samo danas"], ["end", "Do datuma"]].map(([value, label]) => <Button key={value} variant={mode === value ? "default" : "outline"} aria-pressed={mode === value} className="min-h-11" onClick={() => setMode(value!)}>{label}</Button>)}</div></fieldset>
        {mode === "end" && <label className="block">Do<input className={control} type="date" min={start} value={end} onChange={e => setEnd(e.target.value)} /></label>}
      </>}
      {(!created || !uploaded) && <label className="block">Slikaj ili dodaj doznaku (neobavezno)<input className={control} type="file" accept="image/jpeg,image/png,application/pdf" capture="environment" disabled={busy} onChange={e => setFile(e.target.files?.[0] ?? null)} /></label>}
      {error && <p role="alert" className="text-red-800">{created && "Prijava je sačuvana. "}{error}</p>}
      {!created && <Button className="min-h-11 w-full" disabled={busy || !start || (mode === "end" && (!end || end < start))} onClick={() => void submit()}>{busy ? "Spremanje…" : "Prijavi bolovanje"}</Button>}
      {created && <div className="flex flex-wrap gap-2">
        {file && !uploaded && <Button className="min-h-11" disabled={busy} onClick={() => void submit()}>Pokušaj dodati doznaku</Button>}
        <Button className="min-h-11" variant="outline" disabled={busy} onClick={() => void undo()}>Poništi</Button>
        <Button className="min-h-11" disabled={busy} onClick={onClose}>Zatvori</Button>
      </div>}
      {!created && <p className="text-sm">Dani bolovanja upisuju se u evidenciju sati. Doznaku možete dodati i kasnije.</p>}
    </div>
  </Dialog>;
}
